import {
  assoc,
  compose,
  CorrectLens,
  curry,
  lensIndex,
  lensProp,
  map,
  range,
  view
} from "ramda";
import { ReduceNode, reduceTreeChildren } from "../../../utils/mapReduceTree";
import {
  AccountId,
  Spread,
  SpreadAccount,
  SpreadFinancialValue,
  SpreadSheet
} from "../spread/types";
import { AccountHierarchy, reduceAccountHierarchy } from "./accountHierarchy";
import {
  spreadBalanceSheetsLens,
  spreadCashFlowsLens,
  spreadIncomeStatementsLens
} from "./lenses";
import { AccountMap } from "./types";

export type SpreadFinancialValueLens = CorrectLens<
  Spread,
  SpreadFinancialValue
>;

type SpreadFinancialValuesLenses = AccountMap<SpreadFinancialValueLens>;

export enum SpreadFinancialValuesStatus {
  present,
  empty
}

interface SpreadFinancialValuesEmpty {
  type: SpreadFinancialValuesStatus.empty;
}

interface SpreadFinancialValuesPresent {
  type: SpreadFinancialValuesStatus.present;
  spread: Spread;
  lensesByPeriod: SpreadFinancialValuesLenses[];
}

export type SpreadFinancialValues =
  | SpreadFinancialValuesEmpty
  | SpreadFinancialValuesPresent;

const childLenses = (
  spread: Spread,
  lens: CorrectLens<Spread, SpreadAccount[]>
) =>
  map(
    index =>
      compose(
        lens,
        lensIndex(index)
      ),
    range(0, view(lens, spread).length)
  );

const childLensesFromLens = curry(
  (spread: Spread, lens: CorrectLens<Spread, SpreadAccount>) =>
    childLenses(
      spread,
      compose(
        lens,
        lensProp("children")
      )
    )
);

const buildSpreadFinancialValuesLenses = (
  spread: Spread,
  baseLens: CorrectLens<Spread, SpreadAccount[]>
): SpreadFinancialValuesLenses =>
  reduceTreeChildren(
    childLensesFromLens(spread),
    (
      acc: SpreadFinancialValuesLenses,
      lens: CorrectLens<Spread, SpreadAccount>
    ) =>
      assoc(
        view(lens, spread).id,
        compose(
          lens,
          lensProp("value")
        ),
        acc
      ),
    {}
  )(childLenses(spread, baseLens));

export const buildSpreadFinancialValues = curry(
  (
    baseLens: CorrectLens<Spread, SpreadSheet[]>,
    spread: Spread | undefined
  ): SpreadFinancialValues => {
    if (!spread) {
      return { type: SpreadFinancialValuesStatus.empty };
    }
    const spreadSheets = view(baseLens, spread);
    return {
      type: SpreadFinancialValuesStatus.present,
      spread,
      lensesByPeriod: map(
        index =>
          buildSpreadFinancialValuesLenses(
            spread,
            compose(
              baseLens,
              lensIndex(index),
              lensProp("node"),
              lensProp("children")
            )
          ),
        range(0, spreadSheets.length)
      )
    };
  }
);

export const buildIncomeStatementFinancialValues = buildSpreadFinancialValues(
  spreadIncomeStatementsLens
);

export const buildBalanceSheetFinancialValues = buildSpreadFinancialValues(
  spreadBalanceSheetsLens
);

export const buildCashFlowFinancialValues = buildSpreadFinancialValues(
  spreadCashFlowsLens
);

export const getValuesById = curry(
  (
    spreadFinancialValues: SpreadFinancialValues,
    accountId: AccountId
  ): SpreadFinancialValue[] => {
    switch (spreadFinancialValues.type) {
      case SpreadFinancialValuesStatus.present:
        return map(
          lenses => view(lenses[accountId], spreadFinancialValues.spread),
          spreadFinancialValues.lensesByPeriod
        );
      case SpreadFinancialValuesStatus.empty:
        return [];
    }
  }
);

export const getLensesById = curry(
  (
    spreadFinancialValues: SpreadFinancialValues,
    accountId: AccountId
  ): SpreadFinancialValueLens[] => {
    switch (spreadFinancialValues.type) {
      case SpreadFinancialValuesStatus.present:
        return map(
          lenses => lenses[accountId],
          spreadFinancialValues.lensesByPeriod
        );
      case SpreadFinancialValuesStatus.empty:
        return [];
    }
  }
);

export interface SpreadFinancialValueNode {
  accountId: AccountId;
  value: SpreadFinancialValue;
  lens: SpreadFinancialValueLens;
}

export const mapReduceSpreadFinancialValues = <ReducedType>(
  accountHierarchy: AccountHierarchy,
  spreadFinancialValues: SpreadFinancialValues,
  reduceValues: ReduceNode<SpreadFinancialValueNode, ReducedType>,
  initialReducedValue: ReducedType
) => {
  switch (spreadFinancialValues.type) {
    case SpreadFinancialValuesStatus.present:
      return map(
        (lenses: SpreadFinancialValuesLenses) =>
          reduceAccountHierarchy(
            accountHierarchy,
            (acc, accountId) =>
              reduceValues(acc, {
                accountId,
                value: view(lenses[accountId], spreadFinancialValues.spread),
                lens: lenses[accountId]
              }),
            initialReducedValue
          ),
        spreadFinancialValues.lensesByPeriod
      );
    case SpreadFinancialValuesStatus.empty:
      return [];
  }
};



// WEBPACK FOOTER //
// ./src/spread/model/derivedModel/spreadFinancialValues.ts